// src/context/ThemeContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
  useCallback, // 👈 Importe useCallback
} from "react";

export type Theme = {
  primary?: string;
  secondary?: string;
  third?: string;
  menu_buttons_selection?: string;
  menu_buttons_selection_effect?: string;
  [key: string]: any;
};

type ThemeContextValue = {
  theme: Theme | null;
  setTheme: (theme: Theme | null) => void;
  systemTitle: string;
  setSystemTitle: (title: string) => void;
};

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

type ThemeProviderProps = {
  children: ReactNode;
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  // estado inicial vindo do localStorage (evita flash azul)
  const [theme, setThemeState] = useState<Theme | null>(() => {
    // Renomeado para evitar conflito
    try {
      const raw = localStorage.getItem("eva_theme");
      return raw ? (JSON.parse(raw) as Theme) : null;
    } catch {
      return null;
    }
  });

  const [systemTitle, setSystemTitleState] = useState<string>(() => {
    // Renomeado para evitar conflito
    try {
      const saved = localStorage.getItem("eva_system_title");
      return saved || "Eva - googlemarine";
    } catch {
      return "Eva - googlemarine";
    }
  });

  // 1. Função setTheme estável
  const handleSetTheme = useCallback(
    (t: Theme | null) => {
      try {
        if (t) {
          localStorage.setItem("eva_theme", JSON.stringify(t));
        } else {
          localStorage.removeItem("eva_theme");
        }
      } catch {
        /* ignore */
      }
      setThemeState(t); // Chama a função de setState estável
    },
    [setThemeState]
  ); // Dependência: A função de setState do React (que é estável)

  // 2. Função setSystemTitle estável
  const handleSetSystemTitle = useCallback(
    (title: string) => {
      try {
        localStorage.setItem("eva_system_title", title);
      } catch {
        /* ignore */
      }
      setSystemTitleState(title); // Chama a função de setState estável
    },
    [setSystemTitleState]
  ); // Dependência: A função de setState do React (que é estável)

  // 3. O useMemo agora só depende dos estados de valor (que mudam de forma esperada)
  const value = useMemo(
    () => ({
      theme,
      setTheme: handleSetTheme,
      systemTitle,
      setSystemTitle: handleSetSystemTitle,
    }),
    [theme, systemTitle, handleSetTheme, handleSetSystemTitle] // Depende dos valores e das funções estáveis
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme deve ser usado dentro de <ThemeProvider>");
  }
  return ctx;
};
